import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { getActiveCurrency } from '../../Shared/Formatters';

/**
 * Time-series line chart with the anomaly point highlighted.
 *
 * Reads context.series ([{date, value}, ...]), context.marker ({date, value}),
 * and context.baseline (number, optional median line).
 */
export default function SalesAnomalyChart({ context }) {
    const { t } = useTranslation();
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const series = Array.isArray(context?.series) ? context.series : [];
    const marker = context?.marker || null;
    const baseline = typeof context?.baseline === 'number' ? context.baseline : null;
    const metric = context?.metric || 'net_sales';
    const isCount = metric === 'transaction_count';
    const seriesName = isCount ? t('Transactions') : t('Net Sales');
    const cur = getActiveCurrency();

    if (!series.length) {
        return <div style={{ fontSize: 12, color: 'var(--v3-text-muted)' }}>{t('No time series available.')}</div>;
    }

    const chartSeries = [{
        name: seriesName,
        data: series.map(p => ({ x: p.date, y: Number(p.value) || 0 })),
    }];

    const annotations = {
        points: marker ? [{
            x: marker.date,
            y: Number(marker.value) || 0,
            marker: { size: 7, fillColor: '#EF4444', strokeColor: '#fff', strokeWidth: 2 },
            label: {
                borderColor: '#EF4444',
                style: { color: '#fff', background: '#EF4444', fontSize: '10px' },
                text: t('Anomaly'),
            },
        }] : [],
        yaxis: baseline != null ? [{
            y: baseline,
            borderColor: '#06B6D4',
            strokeDashArray: 4,
            label: {
                borderColor: '#06B6D4',
                style: { color: '#fff', background: '#06B6D4', fontSize: '10px' },
                text: t('baseline {{value}}', { value: isCount ? Math.round(baseline).toLocaleString() : `${cur} ${Math.round(baseline).toLocaleString()}` }),
            },
        }] : [],
    };

    const options = {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'Inter, system-ui', background: 'transparent' },
        colors: ['#3B82F6'],
        stroke: { width: 2, curve: 'smooth' },
        grid: { borderColor: 'var(--v3-chart-grid, rgba(255,255,255,0.06))', strokeDashArray: 4 },
        xaxis: { type: 'category', labels: { style: { colors: 'var(--v3-text-muted)', fontSize: '10px' } } },
        yaxis: {
            title: { text: seriesName, style: { color: 'var(--v3-text-muted)', fontSize: '11px', fontWeight: 500 } },
            labels: {
                style: { colors: 'var(--v3-text-muted)', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace' },
                formatter: (v) => isCount
                    ? Math.round(v).toLocaleString()
                    : (Math.abs(v) >= 1000 ? `${cur} ${(v / 1000).toFixed(1)}k` : `${cur} ${Math.round(v)}`),
            },
        },
        tooltip: { theme: isDark ? 'dark' : 'light' },
        dataLabels: { enabled: false },
        legend: { show: false },
        annotations,
    };

    return (
        <div style={{ height: 220 }}>
            <Chart type="line" options={options} series={chartSeries} height={220} />
        </div>
    );
}
